import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  CubeIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useAdmin } from '../../contexts/AdminContext';
import logo from '../../assets/images/logo-white.svg';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  current: boolean;
  action?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAdmin();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation: NavigationItem[] = [
    { name: 'Products', href: '/admin', icon: CubeIcon, current: location.pathname === '/admin' || location.pathname === '/admin/products' },
    { name: 'Sign Out', href: '#', icon: ArrowRightOnRectangleIcon, current: false, action: handleLogout },
  ];

  const publicLinks = [
    { name: 'Visit Website', href: '/', icon: GlobeAltIcon },
    { name: 'Contact Support', href: '/contact', icon: ChatBubbleLeftRightIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-44 bg-gray-900 border-r border-gray-800 flex flex-col shadow-xl">
        <div className="px-4 py-4 border-b border-gray-700">
          <div className="flex flex-col items-center">
            <img src={logo} alt="Electus Creative" className="h-auto w-40 mb-5" />
            <span className="text-lg font-semibold text-white">Admin Panel</span>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
          <div className="mb-6">
            <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Admin Menu</h3>
            <div className="space-y-1">
              {navigation.map((item) => (
                item.action ? (
                  <button
                    key={item.name}
                    onClick={item.action}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 w-full text-left ${
                      item.current
                        ? 'bg-accent text-white shadow-sm'
                        : 'text-gray-300 hover:bg-red-600 hover:text-white'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      item.current
                        ? 'bg-accent text-white shadow-sm'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-4">
            <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Public Site</h3>
            <div className="space-y-1">
              {publicLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center px-3 py-2.5 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-all duration-200"
                >
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col ml-44">
        {/* Page content */}
        <main className="flex-1 min-h-0">
          <div className="py-10 px-6">
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
